import type { BackupHealth } from '@anytracker/core'
import { useNavigate } from 'react-router-dom'

import { Button } from '../components/Button.js'
import styles from './BackupPrompt.module.css'
import { useBackupHealth } from './useBackupHealth.js'

/**
 * The backup nag, at three volumes (T-503).
 *
 * ADR-0003's third durability mechanism. Tone is the whole design problem: too
 * quiet and it is ignored until it matters, too loud and it gets dismissed
 * reflexively — which is worse, because it trains the habit of dismissing.
 *
 * So the level is decided in core from days elapsed, entries at stake and
 * whether the browser may evict the data, and only the volume is decided here:
 *
 *   - **gentle** — one line of text, no dismissal, easy to ignore and meant to
 *     be. There is nothing to dismiss because there is nothing in the way.
 *   - **firm** — a bordered banner with a button, dismissible, and it stays
 *     dismissed until the situation gets worse.
 *   - **urgent** — the same banner in the danger colour, and it comes back a
 *     day after being dismissed.
 */
export function BackupPrompt() {
  const navigate = useNavigate()
  const { health, dismiss } = useBackupHealth()

  if (health?.visible !== true) {
    return null
  }

  if (health.urgency === 'gentle') {
    return (
      <p className={styles['gentle']} data-testid="backup-prompt" data-urgency="gentle">
        {health.headline}.{' '}
        <button
          type="button"
          className={styles['inlineAction']}
          onClick={() => void navigate('/backup')}
        >
          Back up now
        </button>
      </p>
    )
  }

  return <Banner health={health} onDismiss={() => void dismiss()} />
}

function Banner({
  health,
  onDismiss,
}: {
  readonly health: BackupHealth
  readonly onDismiss: () => void
}) {
  const navigate = useNavigate()
  const urgent = health.urgency === 'urgent'

  return (
    <section
      className={`${styles['banner']} ${urgent ? styles['urgent'] : ''}`}
      data-testid="backup-prompt"
      data-urgency={health.urgency}
      // Polite, not assertive. This is important and it is not an emergency —
      // interrupting a screen reader mid-sentence to say it would be the
      // audible version of shouting.
      role="status"
    >
      <p className={styles['headline']}>{health.headline}</p>
      <p className={styles['detail']}>{health.detail}</p>
      <div className={styles['actions']}>
        <Button
          variant={urgent ? 'danger' : 'primary'}
          onClick={() => void navigate('/backup')}
        >
          Back up now
        </Button>
        {/* Dismissable at both levels. A warning that cannot be put down is one
            people work around rather than act on — and the level is recorded,
            so it returns when the situation changes. */}
        <Button variant="ghost" onClick={onDismiss}>
          Not now
        </Button>
      </div>
    </section>
  )
}
